// homeDefaultWidgets.js — the one decision behind "which widgets does a
// workspace's home board show BEFORE anybody personalises it?"
//
// Two answers, and the workspace's install shape picks between them.
//
//   A GENERAL workspace ships the house default set: the greeting card plus
//   the app-shortcut icons (`_DEFAULT_VISIBLE_WIDGETS` in pages/main.js).
//   Everything else in the catalog is opt-in through the Customize drawer.
//
//   An APP-MODE workspace — one whose `settings.workspaceModule` carries
//   `installMode: 'app'` — exists to run ONE installed app. Nika, 2026-09-10:
//   its board lists the MODULE's declared widgets by default and nothing
//   else.
//   Nika, 2026-10-03 (answer A, "Greeting + house dock"): the page FRAME —
//   the greeting with its summary and the Dock's house apps — shows there
//   like on every other workspace; the rule above now covers the BOARD only.
//   The house widgets are not removed and not forbidden; they are
//   simply not part of that workspace's default board. Every one of them
//   stays in the Customize drawer, and adding one back is an ordinary
//   gesture that persists like any other.
//
// WHY THIS IS A DEFAULT AND NOT A HIDE. The board's composed map stacks three
// kinds of fact (see functions/homeHiddenPersist.js): defaults, user choices,
// render clamps. Expressing the app-mode rule as a DEFAULT — a base map every
// resolver re-derives — keeps it in the first kind. Writing the same
// house-widget hides into the gesture ledger or the prefs row would put a
// default in the second kind, which is the exact defect WS-HOME-PREFS-DURABLE-1
// fixed: a stored default outranks every future default for that member,
// permanently, because the read path composes `{ ...base, ...saved }`.
// `persistableHiddenMap` therefore keeps dropping anything that agrees with
// the base, and this file supplies that base.
//
// PRECEDENCE. A workspace that names its own `home_default_panels` still wins
// for what that set can SAY: it is an admin's (or a module's) explicit
// configuration for one workspace, more specific than any default, and
// `_resolveBootHidden` consults it first. What it cannot say — the house
// default-visible ids (the greeting, Next up, the App strip shortcuts), which
// no panel-set writer ever offered — follows the GLOBAL default below
// (`panelSetHiddenMap`, DESIGN-WL-HOME-DEFAULTS-ALL-WORKSPACES-1: the
// redesign's defaults reach every workspace, its personalisation stays). A
// takeover module's panel set keeps its old total reading until Nika decides
// (`takeoverModuleRef`).
//
// Pure by design (the predicate aside): the caller supplies the id lists, so
// a test can pin the decision without a browser, a grid or a network.

import _wsShared from '@symbo-ls/workspace-shared'

/**
 * The APP-MODE install REF for this workspace, or `null`.
 *
 * WHICH PLANE THIS READS, and why the distinction is the whole bug.
 * The shell holds TWO objects whose names differ by one letter:
 *
 *   `root.workspace.settings`  — the Mongo workspace RECORD's settings bag.
 *       The install ref lives HERE, and nowhere else. Measured on the real
 *       Vault workspace 6a9d6f43208bdbbaed744900 (2026-09-10): keys
 *       `appAccessControl, designSystem, featureFlags, language,
 *       languagePolicy, workspaceApps, workspaceModule`.
 *
 *   `root.workspaceSettings`   — the RESOLVED CONFIG bag, computed and
 *       published by applyWorkspaceConfig (functions/workspaceConfigs.js).
 *       It is a DEFAULT_CONFIG-shaped object, so it carries `navbar`,
 *       `modules`, `apps`, `home_default_panels`, `designSystem`,
 *       `language`, `layoutDirection`, `workspace_id` — and NEVER
 *       `workspaceModule`. Same measurement, same workspace, live in the
 *       booted shell: `root.workspaceSettings.workspaceModule` is
 *       `undefined`.
 *
 * The first version of this predicate read the second plane and was
 * therefore INERT on every real workspace: it returned `false` on Vault
 * while the record plainly carried `installMode: 'app'`, so the house
 * widgets were never stood down and Nika hid them by hand. It had been
 * verified against a stand-in state shaped `{ workspaceModule: { installMode:
 * 'app' } }`, which no plane in the shell ever produces.
 *
 * So this reads the SAME expression the shell's own module loader decides on
 * — `workspaceConfigs.js`'s `_isAppModeInstall`, the function that strips
 * `home_default_panels` for an app-mode install. The record REF first,
 * through the ONE accessor for that bag (spec §5 —
 * shared/functions/workspaceSettings.js), then the resolved bag as a
 * fallback for the admin console's optimistic in-flight writes, which stage
 * a next-settings object there ahead of the network round trip. One
 * expression, two call sites, no third opinion.
 *
 * THE PLURAL PLANE IS DELIBERATELY NOT THIS RULE. Every additive install
 * writes `installMode: 'app'` onto its own `settings.workspaceApps[]` entry
 * too (workspaceModuleRef.js `buildWorkspaceModuleRef`). Treating that as
 * app mode would mean installing one marketplace app onto an ordinary
 * workspace erases its whole home board — the exact takeover Nika rejected
 * for the navbar on 2026-07-23. App mode is the SINGULAR
 * `settings.workspaceModule` ref: the workspace that exists to run one app.
 *
 * A real full-takeover client module (greenig / bellforge / financials
 * class) carries NO `installMode` flag and is not app mode: its dashboard is
 * governed by the workspace's authoritative `home_default_panels`, exactly
 * as before.
 *
 * DECISION site, not a render site, so it deliberately does not branch on the
 * readiness bit: "absent" and "not yet loaded" both mean "not app mode", which
 * is the general-workspace default — the safe answer for both meanings. (The
 * accessor already collapses a not-ready record to `settings: null`, so a
 * list-shaped record cannot leak a half-read ref through here.) The home board
 * re-resolves visibility whenever `workspace` / `workspaceSettings` change (the
 * WidgetVisibilitySync watcher), so a late arrival converges rather than
 * sticking.
 *
 * @param {object} root the DOMQL root element's state
 * @returns {object|null} the install ref, or null when this is not app mode
 */
export const appModeModuleRef = (root) => {
  // Deferred barrel read — a module-scope destructure of `_wsShared.functions`
  // runs inside the shared-barrel init cycle, where the default export is
  // still undefined, and killed the whole shell boot twice (2026-08-12). Same
  // arrow idiom as workspaceProjects.js / workspaceConfigs.js.
  const read = _wsShared && _wsShared.functions && _wsShared.functions.workspaceSettings
  const bag = read ? read(root) : null
  const ref =
    (bag && bag.settings && bag.settings.workspaceModule) ||
    (root && root.workspaceSettings && root.workspaceSettings.workspaceModule) ||
    null
  return ref && ref.installMode === 'app' ? ref : null
}

/**
 * Is this workspace an APP-MODE install? See `appModeModuleRef` above for the
 * plane this reads and why the other one is wrong.
 *
 * @param {object} root the DOMQL root element's state
 * @returns {boolean}
 */
export const isAppModeWorkspace = (root) => !!appModeModuleRef(root)

/**
 * The TAKEOVER module REF for this workspace, or `null` — the singular
 * `settings.workspaceModule` WITHOUT `installMode: 'app'`: a client module
 * that owns the workspace's home (the greenig / bellforge / financials class,
 * workspaceConfigs.js `resolveWorkspaceConfig` branch 1). Its panel set is the
 * module's own design of the board, so it keeps governing every catalog id
 * (`panelSetHiddenMap` below) — Nika decides whether the redesign's house
 * defaults reach these workspaces too (DESIGN-WL-HOME-DEFAULTS-ALL-WORKSPACES-1,
 * DECISIONS). An app-mode ref is `appModeModuleRef`'s, never this one.
 *
 * Same plane and accessor as `appModeModuleRef` — the record REF through the
 * spec §5 settings accessor, then the resolved bag the admin console stages
 * optimistic writes on — restated rather than shared: every export here is
 * registered on its own (functions/index.js), so a bare sibling read would
 * revive as a ReferenceError (FA204).
 *
 * @param {object} root the DOMQL root element's state
 * @returns {object|null} the install ref, or null when no takeover module is declared
 */
export const takeoverModuleRef = (root) => {
  // Deferred barrel read — the same idiom as appModeModuleRef above.
  const read = _wsShared && _wsShared.functions && _wsShared.functions.workspaceSettings
  const bag = read ? read(root) : null
  const ref =
    (bag && bag.settings && bag.settings.workspaceModule) ||
    (root && root.workspaceSettings && root.workspaceSettings.workspaceModule) ||
    null
  return ref && typeof ref === 'object' && ref.installMode !== 'app' ? ref : null
}

/**
 * The BASE hidden map of a workspace that names its own panel set
 * (`home_default_panels`), for every id in the live catalog.
 *
 * WHAT A PANEL SET CAN SAY. Every writer of `home_default_panels` speaks a
 * closed vocabulary, and none of them contains the house DEFAULT-VISIBLE ids
 * (`_DEFAULT_VISIBLE_WIDGETS` in pages/main.js: the greeting, Next up and the
 * App strip shortcuts):
 *   • /admin/dashboard-defaults offers the eleven original house panels
 *     (received … broadcast) plus the installed modules' widgets, starts from
 *     `['greeting']` (the 2026-06-16 greeting-only default) and always keeps
 *     the greeting;
 *   • the /admin/module Panels tab mirrors that list;
 *   • a module manifest's `configDefaults.home_default_panels` names the
 *     module's own widgets.
 * So a panel set's SILENCE about Next up or an App strip shortcut was never a
 * choice — the id was not on the writer's menu, and Next up did not exist
 * before 2026-09-27. Reading that silence as "hidden" is what kept the
 * redesign's App strip and Next up off every workspace an admin had ever
 * configured, while a set naming only the greeting (treated as unset by
 * resolveHomePanelSet) showed them: the same writer, two opposite outcomes.
 *
 * THE RULE, per catalog id:
 *   named by the panel set               → visible (`false`) — the admin's
 *                                          or the module's explicit choice;
 *   a house default id, the set silent   → the GLOBAL default for this
 *                                          workspace (`globalHidden`: shown on
 *                                          a general workspace, off in app
 *                                          mode, the curated demo set);
 *   anything else, the set silent        → hidden (`true`), exactly as before.
 * `panelsGovernHouseDefaults` restores the old total reading for one
 * workspace kind: a TAKEOVER module (`takeoverModuleRef`), whose panel set is
 * the module's own board, keeps governing every id until Nika answers.
 *
 * Shape: an EXPLICIT boolean for every catalog id — the shape the old inline
 * expression produced (`catalog.map((w) => [w.id, !panels.includes(w.id)])`),
 * which `_foldAppWidgetDefaults` and the durability filter read through
 * `!!base[id]`. It is a DEFAULT, never written anywhere (the durability law,
 * functions/homeHiddenPersist.js).
 *
 * Pure: the caller supplies the ids and the global map. Self-contained
 * (FA204): it reads no sibling export.
 *
 * @param {object}   args
 * @param {string[]} args.catalogIds       every id in homeCatalogFor(root)
 * @param {string[]} args.panels           the resolved panel set (never null here)
 * @param {string[]} args.houseDefaultIds  `_DEFAULT_VISIBLE_WIDGETS`
 * @param {object}   args.globalHidden     the global default map for this workspace
 * @param {boolean}  args.panelsGovernHouseDefaults  a takeover module's panel set
 * @returns {object} `{ [id]: boolean }` for every catalog id
 */
export const panelSetHiddenMap = ({
  catalogIds = [],
  panels = [],
  houseDefaultIds = [],
  globalHidden = {},
  panelsGovernHouseDefaults = false
} = {}) => {
  const named = new Set(Array.isArray(panels) ? panels : [])
  const house = new Set(
    panelsGovernHouseDefaults || !Array.isArray(houseDefaultIds) ? [] : houseDefaultIds
  )
  const fallback = globalHidden && typeof globalHidden === 'object' ? globalHidden : {}
  const out = {}
  for (const id of Array.isArray(catalogIds) ? catalogIds : []) {
    if (named.has(id)) out[id] = false
    else if (house.has(id)) out[id] = !!fallback[id]
    else out[id] = true
  }
  return out
}

/**
 * The house rows' DEFAULT hidden map for one workspace.
 *
 * Shape note, and it is load-bearing: a default-VISIBLE id gets NO entry at
 * all rather than an explicit `false`. That is what the general branch has
 * always produced (`_DEFAULT_HIDDEN_WIDGETS.map(id => [id, true])`), and the
 * composed read is `!!hidden[id]`, so an absent key and `false` render the
 * same. Keeping the shape identical is what makes the general workspace's
 * behaviour byte-for-byte unchanged by this file.
 *
 * APP MODE (Nika 2026-10-03, DESIGN-WL-HOME-DEFAULTS-TAKEOVER-APP-MODE-1
 * answer A, "Greeting + house dock"): the page FRAME keeps its defaults — the
 * greeting with its summary, and the App strip's house apps — like every other
 * workspace; every house BOARD card stands down (Next up included), so the
 * board is the module's widgets alone. `frameIds` are the catalog rows that
 * render in the frame (`place` in functions/homeWidgetCatalog.js); a frame row
 * a general workspace hides by default (app-infra) stays hidden here too.
 *
 * @param {object} args
 * @param {string[]} args.houseIds          EVERY house catalog id
 * @param {string[]} args.defaultHiddenIds  the ids a GENERAL workspace hides
 * @param {boolean}  args.appMode           isAppModeWorkspace(root)
 * @param {string[]} args.frameIds          the house rows of the page frame
 * @returns {object} `{ [id]: true }` for every id hidden by default
 */
export const houseDefaultHiddenMap = ({
  houseIds = [],
  defaultHiddenIds = [],
  appMode = false,
  frameIds = []
} = {}) => {
  const generalHidden = Array.isArray(defaultHiddenIds) ? defaultHiddenIds : []
  let ids = generalHidden
  if (appMode) {
    const frame = new Set(Array.isArray(frameIds) ? frameIds : [])
    const offEverywhere = new Set(generalHidden)
    ids = (houseIds || []).filter((id) => !frame.has(id) || offEverywhere.has(id))
  }
  const out = {}
  for (const id of ids) out[id] = true
  return out
}

// THE RESET WRITES NO CHOICES. The Customize drawer's "Reset" (pages/main.js)
// sends `hidden_widgets: {}` and `grid_layout: null`, and the board composes
// this file's defaults over that — so a reset means the same thing as a first
// boot, on every workspace kind. It used to send `resetDefaultHiddenMap`: an
// explicit boolean for every catalog id, the defaults of that day stamped into
// the member's row, which outranks every later default for that member and
// ignored the workspace's panel set (DESIGN-WL-HOME-DEFAULTS-ALL-WORKSPACES-1).
